import { useAppSelector } from '../redux/hooks';
import { capabilityReleaseFor, capabilityReleaseMode } from '../utils/capabilityRelease';
import ROLES from '../utils/roles';
import { useAdminAccess } from './useAdminAccess';
import { useProgramContext } from './useProgramContext';

type Org = Parameters<typeof capabilityReleaseFor>[0];

/**
 * Whether the signed-in user sees the Capability release (`on`), and whether
 * that answer is final yet (`resolved`). `on` is false while the org's details
 * are loading, and in admin mode while /v3/admin/access is, so a screen with a
 * pre-release version to fall back to should wait for `resolved` rather than
 * flash that version first.
 */
export const useCapabilityReleaseState = (): { on: boolean; resolved: boolean } => {
  const org = useAppSelector((state) => state.orgDetails?.data as Org);
  const orgResolved = useAppSelector((state) => state.orgDetails?.data !== undefined || state.orgDetails?.error);
  const { isPlatformAdmin } = useProgramContext();
  const { access, loading: accessLoading } = useAdminAccess();
  const viewerIsAdmin =
    isPlatformAdmin ||
    localStorage.getItem('role') === ROLES.orgAdmin ||
    Boolean(access?.orgAdmin) ||
    (access?.managedTeamIds?.length ?? 0) > 0;
  const on = capabilityReleaseFor(org, viewerIsAdmin);
  const resolved = Boolean(orgResolved) && (on || capabilityReleaseMode(org) !== 'admin' || !accessLoading);
  return { on, resolved };
};

/**
 * Whether the signed-in user sees the Capability release (see
 * utils/capabilityRelease.ts). False while the org's details are loading, so
 * nothing of the release shows until it is known to be on. In admin mode the
 * viewer must be one of the people who set it up: a platform admin, an org
 * admin, or a manager of one of the org's teams (/v3/admin/access, fetched
 * once per sign-in).
 */
const useCapabilityRelease = (): boolean => useCapabilityReleaseState().on;

/**
 * Whether what the release puts in front of members runs: the "Role in <team>"
 * question first of all. Only when the release is on for everyone, never in
 * admin mode, whoever is signed in.
 */
export const useCapabilityReleaseForMembers = (): boolean =>
  useAppSelector((state) => capabilityReleaseMode(state.orgDetails?.data as Org) === 'on');

export default useCapabilityRelease;
